import React, { useMemo, useState } from 'react';
import {
  Container, Card, Table, Form, Badge, Alert, Spinner, Tabs, Tab,
  ButtonGroup, Button, Modal, ProgressBar,
} from 'react-bootstrap';
import {
  useGetWorkScopesQuery,
  useSetOfficeKindMutation,
  useSetUserScopeMutation,
  useSetUserWorkRolesMutation,
} from '../app/api';
import { WORK_AREA_ROLES } from './utils';

// Super Admin -> Work Access.
//
// Two jobs that have to happen in order, so they share a page as tabs rather
// than sitting in separate menu items:
//
//   1. Classify each FSC office as internal or entrepreneur. An unclassified
//      office is invisible to "All internal" / "All entrepreneur" scopes, so
//      skipping this silently narrows everyone's access.
//   2. Give each WORK user their Work roles (which pages) and a scope (whose
//      clients). A WORK user with no scope sees NOTHING — the enforcement
//      layer fails closed on purpose — and one with no area roles sees only
//      the Work Home page.

const MODES = [
  { key: 'offices',          label: 'Specific offices', hint: 'Only the offices you pick below.' },
  { key: 'entrepreneur_all', label: 'All entrepreneur', hint: 'Every office classified as entrepreneur.' },
  { key: 'internal_all',     label: 'All internal',     hint: 'Every office classified as internal.' },
  { key: 'all',              label: 'All offices',      hint: 'Everything, both kinds.' },
];

const KIND_LABEL = { internal: 'Internal', entrepreneur: 'Entrepreneur' };

// The roles managed here: one per Work area, plus WORK_ADMIN. Mirrors
// MANAGED_WORK_ROLES in api/app/db/work_scope_admin.py. WORK itself stays in
// the Admin Panel — holding it is what puts someone on this page.
const WORK_ROLES = [
  ...WORK_AREA_ROLES.map(a => ({ role: a.role, label: a.label, hint: `Can open ${a.label}.` })),
  { role: 'WORK_ADMIN', label: 'Work Admin', hint: 'Elevated actions inside the pages above, e.g. "Log in as" a portal client.' },
];
const AREA_ROLE_NAMES = WORK_AREA_ROLES.map(a => a.role);

function personName(u) {
  const n = `${u.first_name || ''} ${u.last_name || ''}`.trim();
  return n || u.username;
}

export default function WorkAccess() {
  const { data, isLoading, isError, refetch } = useGetWorkScopesQuery();
  const [setKind] = useSetOfficeKindMutation();
  const [setScope] = useSetUserScopeMutation();
  const [setWorkRoles] = useSetUserWorkRolesMutation();

  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const [officeQuery, setOfficeQuery] = useState('');
  const [showUnclassifiedOnly, setShowUnclassifiedOnly] = useState(false);
  const [editing, setEditing] = useState(null); // the user whose offices are being picked

  const offices = useMemo(() => data?.offices || [], [data]);
  const users = data?.users || [];

  const classified = offices.filter(o => o.kind).length;
  const unscoped = users.filter(u => !u.scope_mode).length;
  const noAreas = users.filter(u => !(u.work_roles || []).some(r => AREA_ROLE_NAMES.includes(r))).length;

  const visibleOffices = useMemo(() => {
    const q = officeQuery.trim().toLowerCase();
    return offices.filter(o => {
      if (showUnclassifiedOnly && o.kind) return false;
      if (!q) return true;
      return (o.name || '').toLowerCase().includes(q) ||
             (o.b2b_firm_name || '').toLowerCase().includes(q);
    });
  }, [offices, officeQuery, showUnclassifiedOnly]);

  // RTK tagTypes are inert app-wide in this app, so every mutation refetches
  // rather than relying on invalidation.
  const run = async (key, fn) => {
    setBusy(key);
    setError('');
    try {
      await fn().unwrap();
      await refetch();
    } catch (e) {
      setError(e?.data?.detail || 'Something went wrong.');
    } finally {
      setBusy(null);
    }
  };

  const handleKind = (office, kind) =>
    run(office.fsc_id, () => setKind({ fsc_id: office.fsc_id, kind }));

  const handleMode = (user, mode) => {
    // "Specific offices" needs a selection before it means anything, so it opens
    // the picker instead of saving an empty scope the server would reject.
    if (mode === 'offices') {
      setEditing({ user, selected: new Set(user.offices.map(o => o.fsc_id)) });
      return;
    }
    return run(user.uuid, () => setScope({ admin_uuid: user.uuid, mode }));
  };

  // Sends the person's complete set of Work roles, so a toggle can't race
  // another toggle into a half-applied state.
  const toggleRole = (user, role) => {
    const current = new Set(user.work_roles || []);
    if (current.has(role)) current.delete(role); else current.add(role);
    return run(user.uuid, () => setWorkRoles({ admin_uuid: user.uuid, roles: [...current] }));
  };

  const saveOffices = async () => {
    const { user, selected } = editing;
    setEditing(null);
    await run(user.uuid, () => setScope({
      admin_uuid: user.uuid, mode: 'offices', office_ids: [...selected],
    }));
  };

  if (isLoading) {
    return <Container className="py-4 text-center"><Spinner animation="border" /></Container>;
  }
  if (isError) {
    return <Container className="py-4"><Alert variant="danger">Could not load work access.</Alert></Container>;
  }

  return (
    <Container className="py-4">
      <h3 className="mb-1">Work Access</h3>
      <p className="text-muted" style={{ maxWidth: '46rem' }}>
        Holding the WORK role (Admin Panel) decides whether someone can use the
        Work side at all. Here, their Work roles decide <em>which pages</em> they
        see, and their scope decides <em>whose clients</em>. Pages still in
        beta also need the BETA role.
      </p>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

      {unscoped > 0 && (
        <Alert variant="warning" className="d-flex gap-2 align-items-start">
          <i className="bi bi-exclamation-triangle mt-1" />
          <div>
            <strong>{unscoped} of {users.length} WORK {unscoped === 1 ? 'user has' : 'users have'} no
            scope</strong> and currently {unscoped === 1 ? 'sees' : 'see'} no client data at all.
            Access fails closed on purpose, so nobody gets accidental reach — but
            it also means the Work side looks empty until you assign them below.
          </div>
        </Alert>
      )}

      {noAreas > 0 && (
        <Alert variant="info" className="d-flex gap-2 align-items-start">
          <i className="bi bi-info-circle mt-1" />
          <div>
            <strong>{noAreas} of {users.length} WORK {noAreas === 1 ? 'user has' : 'users have'} no
            Work roles</strong> and only {noAreas === 1 ? 'sees' : 'see'} the Work Home page.
          </div>
        </Alert>
      )}

      <Tabs defaultActiveKey="offices" className="mb-3">
        {/* ── 1. Classify offices ──────────────────────────────────────── */}
        <Tab eventKey="offices" title={`Offices (${classified}/${offices.length} classified)`}>
          <div className="mb-3">
            <ProgressBar
              now={offices.length ? (classified / offices.length) * 100 : 0}
              variant={classified === offices.length ? 'success' : 'warning'}
              style={{ height: '0.5rem' }}
            />
          </div>

          <Alert variant="secondary" className="py-2" style={{ fontSize: '0.88rem' }}>
            An unclassified office is invisible to the “All internal” and “All
            entrepreneur” scopes. Mapping a firm on{' '}
            <a href="/admin/firm-offices">Firms &amp; FSC Offices</a> classifies
            it as entrepreneur automatically.
          </Alert>

          <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
            <Form.Control
              style={{ maxWidth: '20rem' }}
              placeholder="Search offices…"
              value={officeQuery}
              onChange={e => setOfficeQuery(e.target.value)}
            />
            <Form.Check
              type="switch"
              label="Unclassified only"
              checked={showUnclassifiedOnly}
              onChange={e => setShowUnclassifiedOnly(e.target.checked)}
            />
          </div>

          <Card>
            <Table responsive hover className="mb-0 align-middle">
              <thead>
                <tr>
                  <th style={{ width: '34%' }}>Office</th>
                  <th style={{ width: '12%' }}>Households</th>
                  <th style={{ width: '30%' }}>Classification</th>
                  <th>Classified by</th>
                </tr>
              </thead>
              <tbody>
                {visibleOffices.map(o => (
                  <tr key={o.fsc_id}>
                    <td>
                      <div className="fw-semibold">{o.name || o.fsc_id}</div>
                      {o.b2b_firm_name && (
                        <Badge bg="light" text="dark" className="border">
                          B2B firm: {o.b2b_firm_name}
                        </Badge>
                      )}
                    </td>
                    <td>
                      {/* An office with no households grants nothing when
                          classified — worth seeing before deciding. */}
                      <span className={o.household_count ? '' : 'text-muted'}>
                        {o.household_count}
                      </span>
                    </td>
                    <td>
                      <ButtonGroup size="sm">
                        {['internal', 'entrepreneur'].map(k => (
                          <Button
                            key={k}
                            variant={o.kind === k ? 'primary' : 'outline-secondary'}
                            disabled={busy === o.fsc_id}
                            onClick={() => handleKind(o, k)}
                          >
                            {KIND_LABEL[k]}
                          </Button>
                        ))}
                        <Button
                          variant={!o.kind ? 'secondary' : 'outline-secondary'}
                          disabled={busy === o.fsc_id}
                          onClick={() => handleKind(o, null)}
                          title="Unclassified — hidden from both kind-based scopes"
                        >
                          —
                        </Button>
                      </ButtonGroup>
                    </td>
                    <td className="text-muted" style={{ fontSize: '0.8rem' }}>
                      {busy === o.fsc_id
                        ? <Spinner animation="border" size="sm" />
                        : o.classified_by
                          ? <>{o.classified_by}
                              {o.classified_at && <> · {new Date(o.classified_at).toLocaleDateString()}</>}</>
                          : '—'}
                    </td>
                  </tr>
                ))}
                {visibleOffices.length === 0 && (
                  <tr><td colSpan={4} className="text-center text-muted py-4">No offices match.</td></tr>
                )}
              </tbody>
            </Table>
          </Card>
        </Tab>

        {/* ── 2. Assign people ─────────────────────────────────────────── */}
        <Tab eventKey="people" title={`People (${users.length - unscoped}/${users.length} scoped)`}>
          <p className="text-muted" style={{ fontSize: '0.88rem' }}>
            Work role changes take effect straight away — the person may need to
            refresh the page to see their menu change.
          </p>
          <Card>
            <Table responsive hover className="mb-0 align-middle">
              <thead>
                <tr>
                  <th style={{ width: '20%' }}>Employee</th>
                  <th style={{ width: '27%' }}>Work roles</th>
                  <th style={{ width: '30%' }}>Scope</th>
                  <th>Sees</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const isBusy = busy === u.uuid;
                  return (
                    <tr key={u.uuid}>
                      <td>
                        <div className="fw-semibold">{personName(u)}</div>
                        <div className="text-muted" style={{ fontSize: '0.78rem' }}>
                          {u.username}
                          {u.status !== 'ACTIVE' && <> · <Badge bg="secondary">{u.status}</Badge></>}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          {WORK_ROLES.map(wr => {
                            const held = (u.work_roles || []).includes(wr.role);
                            return (
                              <Button
                                key={wr.role}
                                size="sm"
                                variant={held ? 'primary' : 'outline-secondary'}
                                disabled={isBusy}
                                title={wr.hint}
                                aria-pressed={held}
                                onClick={() => toggleRole(u, wr.role)}
                              >
                                {held && <i className="bi bi-check-lg me-1" />}
                                {wr.label}
                              </Button>
                            );
                          })}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex flex-wrap gap-1">
                          {MODES.map(m => (
                            <Button
                              key={m.key}
                              size="sm"
                              variant={u.scope_mode === m.key ? 'primary' : 'outline-secondary'}
                              disabled={isBusy}
                              title={m.hint}
                              onClick={() => handleMode(u, m.key)}
                            >
                              {m.label}
                            </Button>
                          ))}
                          <Button
                            size="sm"
                            variant={u.scope_mode ? 'outline-danger' : 'danger'}
                            disabled={isBusy}
                            title="No access to any client data"
                            onClick={() => run(u.uuid, () => setScope({ admin_uuid: u.uuid, mode: null }))}
                          >
                            None
                          </Button>
                        </div>
                      </td>
                      <td style={{ fontSize: '0.85rem' }}>
                        {isBusy ? <Spinner animation="border" size="sm" /> : (
                          !u.scope_mode
                            ? <span className="text-danger">Nothing</span>
                            : u.scope_mode === 'offices'
                              ? (
                                <span
                                  role="button"
                                  className="text-decoration-underline"
                                  onClick={() => setEditing({
                                    user: u, selected: new Set(u.offices.map(o => o.fsc_id)),
                                  })}
                                >
                                  {u.offices.length} office{u.offices.length === 1 ? '' : 's'}
                                  {u.offices.length > 0 &&
                                    <span className="text-muted"> · {u.offices.map(o => o.name).join(', ')}</span>}
                                </span>
                              )
                              : MODES.find(m => m.key === u.scope_mode)?.hint
                        )}
                      </td>
                    </tr>
                  );
                })}
                {users.length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-center text-muted py-4">
                      Nobody holds the WORK role yet. Assign it in the Admin Panel first.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card>
        </Tab>
      </Tabs>

      {/* Office picker for the "Specific offices" scope. */}
      <Modal show={Boolean(editing)} onHide={() => setEditing(null)} size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '1.1rem' }}>
            Offices for {editing && personName(editing.user)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editing && (
            <>
              <p className="text-muted" style={{ fontSize: '0.85rem' }}>
                They will see clients and documents from these offices only.
              </p>
              {offices.map(o => (
                <Form.Check
                  key={o.fsc_id}
                  type="checkbox"
                  id={`off-${o.fsc_id}`}
                  checked={editing.selected.has(o.fsc_id)}
                  onChange={e => {
                    const next = new Set(editing.selected);
                    if (e.target.checked) next.add(o.fsc_id); else next.delete(o.fsc_id);
                    setEditing({ ...editing, selected: next });
                  }}
                  label={
                    <>
                      {o.name || o.fsc_id}
                      <span className="text-muted" style={{ fontSize: '0.8rem' }}>
                        {' · '}{o.household_count} household{o.household_count === 1 ? '' : 's'}
                        {o.kind && ` · ${KIND_LABEL[o.kind]}`}
                      </span>
                    </>
                  }
                />
              ))}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <span className="text-muted me-auto" style={{ fontSize: '0.85rem' }}>
            {editing?.selected.size || 0} selected
          </span>
          <Button variant="outline-secondary" onClick={() => setEditing(null)}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!editing?.selected.size}
            onClick={saveOffices}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
